import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Eye, ExternalLink, Camera } from 'lucide-react';
import defaultHeroImage from '../../images/logo/Eddy-logo-carte.png';

const LOGO_MARK = "https://customer-assets.emergentagent.com/job_5c120cb0-6c39-4574-8992-e03c5cf62daa/artifacts/9e7hexs6_logo-ed-removebg-preview.png";

export default function PreviewPage() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [cats, sett] = await Promise.all([
        api.getCategories(),
        api.getSettings()
      ]);
      setCategories(cats);
      setSettings(sett);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, []);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPhotos(selectedCategory);
      setPhotos(data);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-medium tracking-tight">Aperçu</h1>
          <p className="text-muted-foreground mt-1">Prévisualisez le rendu de votre portfolio</p>
        </div>
        <Button asChild>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Voir le site
          </a>
        </Button>
      </div>

      {/* Preview Container */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-secondary/50">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Aperçu du portfolio
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="bg-background border-t border-border">
            {/* Mini Hero */}
            <div className="relative h-48 bg-cover bg-center" style={{ backgroundImage: `url(${settings?.hero_image_url || defaultHeroImage})` }}>
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="text-center">
                  <img src={LOGO_MARK} alt="ED" className="h-12 w-auto mx-auto mb-2" />
                  <h2 className="font-serif text-2xl text-white">Eddy Dossou</h2>
                  <p className="text-white/70 text-xs tracking-widest uppercase">Photographe</p>
                </div>
              </div>
            </div>

            {/* Gallery Preview */}
            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-2">Portfolio</p>
                <h3 className="font-serif text-2xl">Mes Réalisations</h3>
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <Button
                  variant={selectedCategory === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(null)}
                  className="rounded-full"
                >
                  Tout
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={selectedCategory === cat.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat.id)}
                    className="rounded-full"
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>

              {/* Photos Grid */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : photos.length === 0 ? (
                <div className="text-center py-8">
                  <Camera className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm">Aucune photo</p>
                  <p className="text-xs text-muted-foreground">Ajoutez des photos dans l'onglet "Photos"</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {photos.slice(0, 8).map((photo) => (
                    <div key={photo.id} className="group relative rounded overflow-hidden aspect-square">
                      <img 
                        src={photo.image_url || photo.image_data} 
                        alt={photo.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors">
                        <div className="absolute bottom-0 left-0 right-0 p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                          <p className="text-white text-xs truncate">{photo.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {photos.length > 8 && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  +{photos.length - 8} autres photos
                </p>
              )}

              {/* About Preview */}
              <div className="mt-8 p-6 bg-secondary/30 rounded-lg text-center">
                <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-2">À propos</p>
                <h3 className="font-serif text-xl mb-3">Qui suis-je ?</h3>
                <p className="text-sm text-muted-foreground">
                  {settings?.about_text || "Photographe professionnel passionné par la capture de moments uniques."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
